import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, BarChart3, TrendingUp, AlertTriangle, RefreshCw } from "lucide-react";
import {
  trackerReportPipeline,
  trackerReportRevenue,
  trackerReportAging,
  STATUS_COLORS,
  PRIORITY_COLORS,
} from "../../../lib/tracker";

const fmtUSD = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const monthLabel = (key) => {
  // key is "YYYY-MM"
  try {
    const [y, m] = key.split("-");
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  } catch {
    return key;
  }
};

const Bar = ({ value, max, color }) => {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-2 rounded-sm overflow-hidden" style={{ background: "var(--ups-muted)" }}>
      <div className="h-full" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
};

const PipelineCard = ({ pipeline }) => {
  const maxStatus = Math.max(1, ...pipeline.by_status.map((r) => r.count));
  const maxPri = Math.max(1, ...pipeline.by_priority.map((r) => r.count));
  return (
    <div className="card-swiss p-5 rounded-sm" data-testid="report-pipeline">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline">Open Pipeline</div>
          <h3 className="font-display text-xl font-bold mt-1">By Status &amp; Priority</h3>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Open</div>
          <div className="font-display text-2xl font-black" style={{ color: "var(--ups-accent)" }}>
            {pipeline.total_open}
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-2">By Status</div>
          <div className="space-y-1.5">
            {pipeline.by_status.map((r) => {
              const c = STATUS_COLORS[r.status] || { border: "#737373" };
              return (
                <div key={r.status} className="grid grid-cols-[1fr_auto] items-center gap-3" data-testid={`report-status-${r.status}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[11px] font-mono truncate">{r.status}</span>
                  </div>
                  <span className="font-mono text-[12px] font-bold tabular-nums">{r.count}</span>
                  <div className="col-span-2"><Bar value={r.count} max={maxStatus} color={c.border} /></div>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-2">By Priority</div>
          <div className="space-y-1.5">
            {pipeline.by_priority.map((r) => {
              const c = PRIORITY_COLORS[r.priority] || { bg: "#737373" };
              return (
                <div key={r.priority} className="grid grid-cols-[1fr_auto] items-center gap-3" data-testid={`report-priority-${r.priority}`}>
                  <span className="text-[11px] font-mono">{r.priority}</span>
                  <span className="font-mono text-[12px] font-bold tabular-nums">{r.count}</span>
                  <div className="col-span-2"><Bar value={r.count} max={maxPri} color={c.bg} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const RevenueCard = ({ revenue, months, setMonths }) => {
  const max = Math.max(1, ...revenue.months.map((m) => m.revenue));
  return (
    <div className="card-swiss p-5 rounded-sm" data-testid="report-revenue">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline">Shipped Revenue</div>
          <h3 className="font-display text-xl font-bold mt-1">Revenue Trend</h3>
          <p className="text-[11px] text-[color:var(--ups-subtext)] mt-0.5">
            Sum of <span className="font-mono">total_cost</span> across jobs that hit “Shipped” status.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={months}
            onChange={(e) => setMonths(parseInt(e.target.value, 10))}
            className="input-swiss px-2 py-1.5 rounded-sm text-[12px]"
            data-testid="report-revenue-months"
          >
            {[3, 6, 12, 24].map((m) => (
              <option key={m} value={m}>
                Last {m} mo
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-end gap-2 h-40 mb-3">
        {revenue.months.map((m) => {
          const h = max > 0 ? Math.max(2, Math.round((m.revenue / max) * 100)) : 0;
          return (
            <div key={m.month} className="flex-1 flex flex-col items-center justify-end" data-testid={`report-revenue-bar-${m.month}`}>
              <div
                className="w-full rounded-t-sm transition-all"
                style={{ height: `${h}%`, background: m.revenue > 0 ? "var(--ups-ink)" : "var(--ups-border)" }}
                title={`${monthLabel(m.month)} · ${fmtUSD(m.revenue)} (${m.jobs} jobs)`}
              />
              <div className="text-[9px] font-mono mt-1 text-[color:var(--ups-subtext)] uppercase">{monthLabel(m.month)}</div>
              <div className="text-[10px] font-mono font-bold">{fmtUSD(m.revenue)}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-3 pt-3 border-t" style={{ borderColor: "var(--ups-border)" }}>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Total Revenue</div>
          <div className="font-display text-xl font-black" style={{ color: "var(--ups-accent)" }}>
            {fmtUSD(revenue.total_revenue)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Shipped Jobs</div>
          <div className="font-display text-xl font-black">{revenue.total_jobs}</div>
        </div>
      </div>
    </div>
  );
};

const AgingCard = ({ aging, threshold, setThreshold }) => (
  <div className="card-swiss p-5 rounded-sm" data-testid="report-aging">
    <div className="flex items-center justify-between mb-4">
      <div>
        <div className="overline">Aging</div>
        <h3 className="font-display text-xl font-bold mt-1 flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-500" />
          Stuck Jobs
        </h3>
        <p className="text-[11px] text-[color:var(--ups-subtext)] mt-0.5">
          Open jobs that haven’t changed status in the last <span className="font-bold">{threshold}</span> day(s).
        </p>
      </div>
      <select
        value={threshold}
        onChange={(e) => setThreshold(parseInt(e.target.value, 10))}
        className="input-swiss px-2 py-1.5 rounded-sm text-[12px]"
        data-testid="report-aging-threshold"
      >
        {[1, 2, 3, 5, 7, 14, 30].map((d) => (
          <option key={d} value={d}>
            &gt; {d} day{d > 1 ? "s" : ""}
          </option>
        ))}
      </select>
    </div>

    {aging.jobs.length === 0 ? (
      <div className="p-6 text-sm text-[color:var(--ups-subtext)] text-center" data-testid="report-aging-empty">
        Nothing stale — all jobs are moving along nicely.
      </div>
    ) : (
      <div className="rounded-sm overflow-hidden" style={{ border: "1px solid var(--ups-border)" }}>
        <table className="w-full text-[12px]">
          <thead style={{ background: "var(--ups-muted)" }}>
            <tr className="text-left">
              {["Job #", "Name", "Status", "Pri", "Days Stuck"].map((h) => (
                <th key={h} className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em]">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {aging.jobs.map((j) => {
              const c = STATUS_COLORS[j.status] || { bg: "#E7E5E4", fg: "#1C1917", border: "#A8A29E" };
              const p = PRIORITY_COLORS[j.priority] || PRIORITY_COLORS.Normal;
              return (
                <tr key={j.id} className="border-t" style={{ borderColor: "var(--ups-border)" }} data-testid={`report-aging-row-${j.id}`}>
                  <td className="px-3 py-2 font-mono text-[11px]">{j.job_number}</td>
                  <td className="px-3 py-2 truncate max-w-[260px]">
                    <div className="font-medium">{j.name}</div>
                    <div className="text-[10px] text-[color:var(--ups-subtext)]">{j.customer_name || "—"}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] rounded-sm whitespace-nowrap"
                      style={{ background: c.bg, color: c.fg, border: `1px solid ${c.border}` }}
                    >
                      {j.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] rounded-sm"
                      style={{ background: p.bg, color: p.fg }}
                    >
                      {j.priority}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">
                    <span className="px-1.5 py-0.5 rounded-sm" style={{ background: "#FEF3C7", color: "#92400E" }}>
                      {j.days_in_status}d
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const ReportsTab = () => {
  const [pipeline, setPipeline] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [aging, setAging] = useState(null);
  const [months, setMonths] = useState(6);
  const [threshold, setThreshold] = useState(3);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [p, r, a] = await Promise.all([
        trackerReportPipeline(),
        trackerReportRevenue(months),
        trackerReportAging(threshold),
      ]);
      setPipeline(p);
      setRevenue(r);
      setAging(a);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [months, threshold]);

  if (loading || !pipeline || !revenue || !aging) {
    return (
      <div className="p-10 text-[color:var(--ups-subtext)] flex items-center" data-testid="reports-loading">
        <Loader2 className="animate-spin mr-2" size={16} />
        Loading reports…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="reports-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-2xl font-bold flex items-center gap-2">
            <BarChart3 size={20} /> Tracker Reports
          </h3>
          <p className="text-sm text-[color:var(--ups-subtext)] mt-1">
            Snapshot of open pipeline, shipped revenue trend, and stuck jobs that need attention.
          </p>
        </div>
        <button
          onClick={reload}
          className="px-3 py-2 text-[11px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 hover:bg-[color:var(--ups-muted)]"
          style={{ border: "1px solid var(--ups-border)" }}
          data-testid="reports-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <PipelineCard pipeline={pipeline} />

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        <RevenueCard revenue={revenue} months={months} setMonths={setMonths} />
        <AgingCard aging={aging} threshold={threshold} setThreshold={setThreshold} />
      </div>

      <div className="text-[11px] font-mono text-[color:var(--ups-subtext)] flex items-center gap-2">
        <TrendingUp size={11} /> Reports refresh on demand. Revenue is computed from the most-recent <span className="font-bold">last_status_change_at</span> for jobs in “Shipped”.
      </div>
    </div>
  );
};

export default ReportsTab;
